import type { ReactNode } from 'react';
import { memo, useCallback, useContext, useMemo, useState } from 'react';
import type { CalendarProps } from 'react-calendar';
import type { TileArgs, Range } from 'react-calendar/src/shared/types';
import './CalendarStyles.css';
import { DateTime } from 'luxon';
import { clsx } from 'clsx';
import { FormattedMessage } from 'react-intl';
import BaseInput from '../text/BaseInput';
import isTileDisabledForDate from '../../../util/date/isTileDisabledForDate';
import type { BaseInputProps } from '../text/BaseInput';
import { SidePane, TooltipStateLess } from '../../popup';
import { HStack } from '../../layout';
import { Button } from '../../buttons';
import { Text } from '../../typography';
import notEmpty from '../../../../core/util/notEmpty';
import LayoutContext from '../../../contexts/LayoutContext';
import { ChevronDownIcon } from '../../icons';
import DateRangeInputCalendar from './DateRangeInputCalendar';
import styles from './DateInput.module.css';

export interface PredefinedDateRange {
    /**
     * Label displayed for the range
     */
    label: ReactNode;
    /**
     * Start date for this date range.
     *
     * The term tentative is used because we select
     * the closest date after this start date (or the day itself) based on which dates
     * are enabled
     */
    tentativeStartDate: Date;
    /**
     * End date for this date range.
     *
     * The term tentative is used because we select
     * the closest date before this end date (or the day itself) based on which dates
     * are enabled
     */
    tentativeEndDate: Date;
}

export type { Range } from 'react-calendar/src/shared/types';

export interface DateRangeInputProps
    extends Omit<BaseInputProps, 'value' | 'onChange'> {
    /**
     * Current value of the date input.
     */
    value?: Range<Date>;
    /**
     * A list of valid dates. Any dates that are not present in this array will be disabled.
     */
    validDates?: string[];
    /**
     * A list of predefined date range options shown alongside the range picker.
     */
    predefinedOptions?: PredefinedDateRange[];
    /**
     * Props that are applied to the calendar component.
     */
    calendarProps?: Pick<
        CalendarProps,
        'minDate' | 'maxDetail' | 'activeStartDate' | 'maxDate'
    >;
    onChange(newValue?: Range<Date>): void;
}

// eslint-disable-next-line max-lines-per-function
const DateRangeInput = ({
    value,
    onChange,
    validDates,
    predefinedOptions,
    calendarProps,
    ...inputProps
}: DateRangeInputProps) => {
    const { isInMobileView } = useContext(LayoutContext);

    const [showDatePicker, setShowDatePicker] = useState(false);
    const [localValue, setLocalValue] = useState<Range<Date | null> | null>(
        value || null
    );

    const tileDisabled = useMemo(
        () =>
            validDates
                ? (props: TileArgs) =>
                      isTileDisabledForDate(
                          props,
                          validDates,
                          value?.filter(notEmpty) ?? []
                      )
                : undefined,
        [validDates, value]
    );

    const onApply = useCallback(() => {
        onChange((localValue as Range<Date> | null) || undefined);
        setShowDatePicker(false);
    }, [localValue, onChange]);

    const onClear = useCallback(() => {
        setLocalValue(null);
        onChange(undefined);
        setShowDatePicker(false);
    }, [onChange]);

    const formattedValue = value
        ? `${DateTime.fromJSDate(value[0]).toFormat(
              'dd.LL.yyyy'
          )} - ${DateTime.fromJSDate(value[1]).toFormat('dd.LL.yyyy')}`
        : null;
    return (
        <>
            <TooltipStateLess
                showArrow
                modal
                mode="click"
                open={!isInMobileView && showDatePicker}
                onOpenChange={setShowDatePicker}
                theme={styles.tooltipTheme}
                placement="bottom"
                content={
                    <DateRangeInputCalendar
                        calendarProps={calendarProps}
                        localValue={localValue}
                        setLocalValue={setLocalValue}
                        onApply={onApply}
                        onClear={onClear}
                        predefinedOptions={predefinedOptions}
                        tileDisabled={tileDisabled}
                        value={value}
                    />
                }
            >
                <div className={styles.trigger}>
                    <BaseInput
                        {...inputProps}
                        value={formattedValue ?? ''}
                        endAdornment={
                            <div
                                className={clsx(styles.triggerIcon, {
                                    [styles.opened]: showDatePicker,
                                })}
                            >
                                <ChevronDownIcon />
                            </div>
                        }
                    >
                        <div>{formattedValue ?? inputProps.placeholder}</div>
                    </BaseInput>
                </div>
            </TooltipStateLess>
            {isInMobileView && (
                <SidePane
                    autoHeight
                    side="bottom"
                    open={showDatePicker}
                    onOpenChange={isOpen => setShowDatePicker(isOpen)}
                    footer={
                        <HStack justify="spaceBetween" gap="small">
                            <Button
                                text
                                inline
                                onClick={onApply}
                                disabled={!localValue}
                            >
                                <FormattedMessage id="Library DateInput Apply" />
                            </Button>
                            <Text secondary>
                                {localValue?.[0] && (
                                    <>
                                        {DateTime.fromJSDate(
                                            localValue[0]
                                        ).toFormat('dd.LL.yyyy')}
                                    </>
                                )}
                                {localValue?.[1] && (
                                    <>
                                        {' '}
                                        -{' '}
                                        {DateTime.fromJSDate(
                                            localValue[1]
                                        ).toFormat('dd.LL.yyyy')}
                                    </>
                                )}
                            </Text>
                            <Button
                                text
                                inline
                                secondary
                                disabled={!value}
                                onClick={onClear}
                            >
                                <FormattedMessage id="Library DateInput Clear" />
                            </Button>
                        </HStack>
                    }
                >
                    <div className="dateInput">
                        <DateRangeInputCalendar
                            calendarProps={calendarProps}
                            localValue={localValue}
                            setLocalValue={setLocalValue}
                            onApply={onApply}
                            onClear={onClear}
                            predefinedOptions={predefinedOptions}
                            tileDisabled={tileDisabled}
                            value={value}
                        />
                    </div>
                </SidePane>
            )}
        </>
    );
};

export default memo(DateRangeInput);
